import React, { ChangeEvent } from "react";
import { useState, useEffect, useFormContext, Hooks, Form, Field, Name, Title, Info, Code, Actions, Toast,  API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/FormImports';
import { Dispense } from "models/DispenseModel";
import { Item, emptyItem } from "models/ItemModel";
import AsyncSelect from "react-select/async";
import { Calculate } from "helpers/CalculateHelpers"
import { useSearchParams } from 'react-router-dom';
import { Inventory } from "models/InventoryModel";
import { Product } from "models/ProductModel";

const DispenseForm: React.FC = () => {
  const width = { product: 28, price: 10, qty: 8, unit: 12, gross: 10, discountP: 5, discountA: 9, depositP: 5, depositA: 9, taxable: 8, taxP:6, taxA:8, netAmount: 12 }
  const [formId, formName] = [Code.Form.Id.DISPENSE, Code.Form.Name.DISPENSE];
  const {keyId, activeId, setActiveId, loading, setLoading, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useFormContext();
  const [activeTab, setActiveTab] = useState<string>(Utility.Storage.GetTab(formName));
  const [userOptions, setUserOptions] = Hooks.Form.Options();
  const [formData, setFormData] = useState<Dispense>({});
  const [unitOptions, setUnitOptions] = Hooks.Form.Options();
  const [segmentOptions, setSegmentOptions] = Hooks.Form.Options();
  const [statusOptions, setStatusOptions] = Hooks.Form.Options();
  const [selectedProduct, setSelectedProduct] = useState<OptionType | null>(null);
  const [localSkucode, setLocalSkucode] = useState<string>('');
  const emptyOptions = Utility.Get.OptionValues([Code.Option.Common.NA]);
  
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get(Code.UrlPara.Client);
  const userId = Utility.Storage.UserId();

  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasOpen(Privilege);
   
  useEffect(() => {

    Files.Json.Http(Code.File.UNIT, Code.Option.Common.NOTHING, setUnitOptions, setError);
    Files.Json.API(Code.File.USER, Code.Option.Common.SELECT, setUserOptions, setError);
    Files.Json.API(Code.File.SEGMENT, Code.Option.Common.SELECT, setSegmentOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.DISPENSE_STATUS, Code.Option.Common.NA, setStatusOptions, setError);
    
    API.Data.GetByKeyId<Dispense>(formName, keyId, setLoading, setError)
    .then(async(data) => { if(data) {
      //console.log(data);
      setActiveId(data.dispense_active); 
      const clientData = await API.Data.getClientNameNumber(data.client_id);
      const formData = await Form.Map.Data(data);
      setFormData((prevData) => ({
        ...prevData,
        ...formData,
        ...clientData, 
      }));
      setLoading(false);
    }})

    const onNewEntry = async () => {
      let clientName ='';
      if(keyId===Code.Key.NEW) {
        if(clientId){
          clientName = await API.Data.GetClientName(Number(clientId));
        }
        setFormData((prevData) => ({
          ...prevData,
          client_id: Number(clientId),
          client_name: clientName ? Utility.Value.Text(clientName) : '', 
          status_id: 1831, //pickup
          segment_id: Code.Option.Segment.OPTICAL,
          user_id: userId,
        }));
      }
      setLoading(false);
    };
    onNewEntry();
  }, []);

  const onItemChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    const isCalculate = Code.Item.NoneCalculateFields.includes(name);
    const updatedValue = isCalculate ? value : (value ? Number(value) || 0 : 0);
  
    setFormData((prevData) => {
      const updatedItems = [...prevData.items!];
      const key = name as keyof Item;
  
      if (name === 'selected') {
        updatedItems[index] = {
          ...updatedItems[index],
          [key]: checked,
          item_active: checked ? 3 : 2, 
        };
      } else {
        updatedItems[index] = {
          ...updatedItems[index],
          [key]: updatedValue,
        };
      }
  
      updatedItems[index] = Calculate.ItemValues(updatedItems[index]);
      const grandTotals = Calculate.GrandTotals(updatedItems,formName);
  
      return {
        ...prevData,
        items: updatedItems,
        dispense_gross_amount: grandTotals.dispense_gross_amount || 0,
        dispense_discount_amount: grandTotals.dispense_discount_amount || 0,
        dispense_deposit_amount: grandTotals.dispense_deposit_amount || 0,
        dispense_taxable_amount: grandTotals.dispense_taxable_amount || 0,
        dispense_tax_amount: grandTotals.dispense_tax_amount || 0,
        dispense_net_amount: grandTotals.dispense_net_amount || 0,
        dispense_payable_amount: grandTotals.dispense_payable_amount || 0,
      };
    });
  };

  const addItem = async () => {
    
    if (localSkucode || (selectedProduct?.value  && selectedProduct?.value > 0) ) {  
      let productId = selectedProduct?.value || 0;
      let productName = selectedProduct?.label || '';
      let specificationId = 2;
      let productComment = '';
      let utnCode = ''
      let skuCode = ''
      let inventoryId = 2;
      let salePrice = 0;
      let quantity = 1;
      let discountPercent = 0;
      let discountAmount = 0;
      let taxPercent = 0;

      const fields = Utility.Join.Fields([
        Name.Inventory.SKU,
        Name.Inventory.UTN,
        Name.Inventory.SerialNo,
        Name.Inventory.Sale,
        Name.Inventory.Data,
        Name.Product.Id,
        Name.Specification.Id,
      ]);

      if(productId == 0){
        const inventoryData = await API.Data.GetInventoryViewById<Inventory>(localSkucode);
        if(inventoryData){
          utnCode = inventoryData.inventory_utncode || '';
          skuCode = inventoryData.inventory_skucode || '';
          specificationId = inventoryData.specification_id || 2;
          productId = inventoryData.product_id || productId;
          inventoryId = inventoryData.inventory_id || inventoryId;
          salePrice = inventoryData.inventory_sale_price || 0;
          productComment = Utility.Get.ProductSummary(inventoryData, specificationId);
          productName = inventoryData.product_name || productName;
          salePrice = inventoryData.product_sale_price || 0;
          discountPercent = inventoryData.offer_percentage || 0;
          discountAmount = inventoryData.offer_amount || 0;
          taxPercent = inventoryData.tax_percentage || 0;
          quantity = inventoryData.product_min_sale || 1;
        }
        else { Utility.Message.Warning(["No inventory found for the entered SKU/UTN"],setMessage); return; }
      }
      else
      {
        const productData = await API.Data.GetProductViewById<Product>(productId);
        if(productData){
          productName = productData.product_name || productName;
          productComment = productData.product_comment || '';
          salePrice = productData.product_sale_price || 0;
          discountPercent = productData.offer_percentage || 0;
          discountAmount = productData.offer_amount || 0;
          taxPercent = productData.tax_percentage || 0;
          quantity = productData.product_min_sale || 1;
        }
        else{ Utility.Message.Warning(["Product not found"],setMessage); return; }
      }

      const newItem: Item = { 
        ...emptyItem, 
        item_utncode: utnCode,
        item_skucode: skuCode,
        inventory_id: inventoryId,
        item_price: Number(salePrice),
        item_quantity: quantity,
        item_net_amount: Number(salePrice * quantity),
        ...(productId !== 0 && {
          product_id: productId,
          product_name: productName,
        }),
        item_comment: productComment,
        stock_status_id: 1,  
        item_discount_percentage: Number(discountPercent),
        item_discount_amount: Number(discountAmount),
        item_tax1_percentage: Number(taxPercent),
      };
      
      setFormData((prev) => {
        const updatedItems = [...(prev.items || []), newItem];
        const newIndex = updatedItems.length - 1;

        const fakeEvent = {
          target: { name: "item_price", value: salePrice, type: "number", checked: false },
        } as unknown as React.ChangeEvent<HTMLInputElement>;

        const updatedItem = Calculate.ItemValues(updatedItems[newIndex]);
        updatedItems[newIndex] = updatedItem;

        const grandTotals = Calculate.GrandTotals(updatedItems, formName);

        return {
          ...prev,
          items: updatedItems,
          dispense_gross_amount: grandTotals.dispense_gross_amount || 0,
          dispense_discount_amount: grandTotals.dispense_discount_amount || 0,
          dispense_deposit_amount: grandTotals.dispense_deposit_amount || 0,
          dispense_taxable_amount: grandTotals.dispense_taxable_amount || 0,
          dispense_tax_amount: grandTotals.dispense_tax_amount || 0,
          dispense_net_amount: grandTotals.dispense_net_amount || 0,
          dispense_payable_amount: grandTotals.dispense_payable_amount || 0,
        };
      });
      

      // TODO: handle inventoryData (set state, update form, etc.)
      //console.log("formData------>",formData);
      setSelectedProduct({value: 0, label: 'Select'});
      setLocalSkucode('');
    }
  };
    
  

  const calculateFunction = (updatedFormData: any) => {
    let roundOffAmount = Number(updatedFormData.dispense_roundoff_amount) || 0;
    let netAmount = Number(updatedFormData.dispense_net_amount) || 0;
    let payableAmount = Number(updatedFormData.dispense_payable_amount) || 0;
  
    payableAmount = netAmount + roundOffAmount;
  
    return {
      ...updatedFormData,
      dispense_payable_amount: Utility.Maths.toDecimal(payableAmount),
    };
  };

  useEffect(() => {
    setFormData((prev) => calculateFunction(prev));
  }, [ formData.dispense_net_amount, formData.dispense_roundoff_amount, formData.dispense_payable_amount ]);

  const onSubmit = async (e: React.FormEvent) => {
    if (!Toast.Validate.Form(e,setMessage)) return;
    const addonData = await API.Data.AddonData(formData, formName, keyId);
    const finalData = Utility.Sanitize.FormData({
      ...formData,
      ...addonData,
    });
    
    const response = await API.Data.Save(formName, keyId, finalData, setMessage);
    if(response){
      //do nothing
    }
  };

  return (
    <Control.Form.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      <Messages.Toast Messages={message} Error={error} />
      <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.Delete(formName, keyId, activeId, setMessage)} onRefuse={()=>{}} />
      {!loading && (
        <form id={Code.Form.Name.EDIT} ref={formRef} onSubmit={onSubmit} autoComplete="off">
          <Button.Save keyId={keyId} activeId={activeId} handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} clientNumber={formData.client_number} formName={formName} formId={formId} Privilege={Privilege} print={true} />
          <Control.Tab.Header titles={["Details", "More"]} activeTab={activeTab} formName={formName} setActiveTab={setActiveTab} />
          <Control.Form.Data>
            
            <Control.Tab.Content id={Code.Tab.Tab1} isActive={activeTab === Code.Tab.Tab1}>
              <Control.Row>
                <Field.Date id={Name.Dispense.Date} value={formData.dispense_date} title={Title.Date} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,20,1]} require={true} currentDate={true} />
                <Field.Text id={Name.Dispense.Number} value={formData.dispense_number} title={Title.SNo} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,20,1]} />
                <Field.Select id={Name.Common.SegmentId} value={formData.segment_id} title={Title.Segment} options={segmentOptions} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,20,1]} />
                <Field.Select id={Name.Common.StatusId} value={formData.status_id} title={Title.Status} options={statusOptions} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,20,1]} />
                <Field.FilterSelect id={Name.User.Id} value={formData.user_id} title={Title.Dispenser} options={userOptions} setFormData={setFormData} size={[1,20,1]} require={true} />
              </Control.Row>
              <Control.Row>
                <Field.Header id={Name.Client.Id} title={Title.Client} require={true}>
                  <AsyncSelect id={Name.Client.Id} loadOptions={(inputValue) => API.Data.AsyncOption(inputValue, Code.Form.Name.CLIENT, Code.Mapping.client.value, Code.Mapping.client.label)} styles={Code.Style.FormOption} onChange={(selected) => Utility.Handle.ClientChange(selected, setFormData)} value={Utility.Value.Async(formData.client_id,formData.client_name)} placeholder="Search" />
                </Field.Header>
              </Control.Row>
              <Control.Row>
                <Field.Header id={Name.Product.Id} title={Title.Product} size={[0,40,1]}>
                  <AsyncSelect id={Name.Product.Id} value={selectedProduct} loadOptions={(inputValue) => API.Data.AsyncOption(inputValue, Code.Form.Name.PRODUCT, Code.Mapping.product.value, Code.Mapping.product.label)} styles={Code.Style.FormOption} onChange={(selected) => setSelectedProduct(selected as OptionType | null)} placeholder="Search" />
                </Field.Header>
                <Field.Text id={Name.Item.SKU} value={localSkucode} title={Title.Skucode} onChange={(e) => setLocalSkucode(e.target.value)} size={[1,20,1]} />
                <Button.FrameAction title="+" info={Info.AddProduct} onClick={addItem} size={[1,20,1]} className='pt6' />
              </Control.Row>
              <Control.Form.Table>
                <Control.Form.Title>
                  <Control.Grid.Items values={[
                    [Title.Product, width.product], 
                    [Title.Price, width.price, Code.Align.RIGHT], 
                    [Title.Qty, width.qty, Code.Align.RIGHT],
                    [Title.Unit, width.unit, Code.Align.CENTER, false],
                    [Title.Gross, width.gross, Code.Align.RIGHT],
                    [Title.Discount, width.discountP + width.depositA, Code.Align.CENTER],
                    [Title.Deposit, width.depositP + width.depositA, Code.Align.CENTER, false],
                    [Title.Taxable, width.taxable, Code.Align.RIGHT, false],
                    [Title.Tax, width.taxP + width.taxA, Code.Align.CENTER],
                    [Title.NetAmount, width.netAmount, Code.Align.RIGHT]
                  ]} first={1} />
                </Control.Form.Title>
                <Control.Form.List>
                  {formData.items?.map((item, index) => (
                    <Control.Form.Index key={index} index={index}>
                      <Control.Form.Item>
                        <Control.Form.Check index={index} checked={item.selected} handleItemChange={(index, e) => onItemChange(index, e)} />
                        <Control.Form.Text id={Name.Product.Name} value={Utility.Format.ProductQuick(item.product_name,item.item_skucode,item.item_utncode)} size={[width.product]} disabled={true} />
                        <Control.Form.Number id={Name.Item.Price} value={item.item_price} size={[width.price]} onChange={(e) => onItemChange(index,e)}  />
                        <Control.Form.Number id={Name.Item.Quantity} value={item.item_quantity} size={[width.qty]} onChange={(e) => onItemChange(index,e)}  />
                        <Control.Form.Option id={Name.Unit.Id} value={item.unit_id} optionValues={unitOptions} size={[width.unit]} onChange={(e) => onItemChange(index,e)} visible={false} />
                        <Control.Form.Number id={Name.Item.Gross} value={item.item_gross_amount} size={[width.gross]} onChange={(e) => onItemChange(index,e)} />
                        <Control.Form.Number id={Name.Item.DiscountP} value={item.item_discount_percentage} size={[width.discountP]} onChange={(e) => onItemChange(index,e)} placeholder="%"  />
                        <Control.Form.Number id={Name.Item.DiscountA} value={item.item_discount_amount} size={[width.discountA]} onChange={(e) => onItemChange(index,e)}  />
                        <Control.Form.Number id={Name.Item.DepositP} value={item.item_deposit_percentage} size={[width.depositP]} onChange={(e) => onItemChange(index,e)} placeholder="%" visible={false} />
                        <Control.Form.Number id={Name.Item.DepositA} value={item.item_deposit_amount} size={[width.depositA]} onChange={(e) => onItemChange(index,e)} visible={false} />
                        <Control.Form.Number id={Name.Item.Taxable} value={item.item_taxable_amount} size={[width.taxable]} onChange={(e) => onItemChange(index,e)} visible={false} />
                        <Control.Form.Number id={Name.Item.Tax1P} value={item.item_tax1_percentage} size={[width.taxP]} onChange={(e) => onItemChange(index,e)} placeholder="%" />
                        <Control.Form.Number id={Name.Item.Tax1A} value={item.item_tax1_amount} size={[width.taxA]} onChange={(e) => onItemChange(index,e)}  />
                        <Control.Form.Number id={Name.Item.Net} value={item.item_net_amount} size={[width.netAmount]} onChange={(e) => onItemChange(index,e)}  /> 
                      </Control.Form.Item>
                      <Control.Form.Item>
                        <Control.Form.TextArea index={index} value={item.item_comment} handleItemChange={(index, e) => onItemChange(index, e)} />
                      </Control.Form.Item>
                    </Control.Form.Index>
                  ))}
                </Control.Form.List>
              </Control.Form.Table>
              <Control.Flex>
                <Control.Column size={[0,50,0,3]}><></></Control.Column>
                <Control.Column size={[0,50,0,1]}>
                  <Control.Row>
                    <Field.Title size={[0,32,1]} />
                    <Field.Amount id={Name.Dispense.Gross} value={formData.dispense_gross_amount} title={Title.Gross} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,34,1]} require={true} />
                    <Field.Amount id={Name.Dispense.Discount} value={formData.dispense_discount_amount} title={Title.Discount} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,34,0]} require={true}  />
                    {/* <Field.Number id={Name.Dispense.DepositAmount} value={formData.dispense_deposit_amount} title={Title.Deposit} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,16,0]} require={true} /> */}
                  </Control.Row>
                  <Control.Row>
                    <Field.Title size={[0,32,1]} />
                    {/* <Field.Number id={Name.Dispense.TaxableAmount} value={formData.dispense_taxable_amount} title={Title.Taxable} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,16,1]} require={true} disabled={true} /> */}
                    <Field.Amount id={Name.Dispense.Tax} value={formData.dispense_tax_amount} title={Title.Tax} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,34,1]} require={true}  />
                    <Field.Amount id={Name.Dispense.Net} value={formData.dispense_net_amount} title={Title.NetAmount} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,34,0]} require={true}  />
                  </Control.Row>
                  <Control.Row>
                    <Field.Title size={[0,32,1]} />
                    <Field.Amount id={Name.Dispense.Roundoff} value={formData.dispense_roundoff_amount} title={Title.Roundoff} onChange={(e) => Utility.Handle.Calculate(e,setFormData,calculateFunction)} size={[1,34,1]} />
                    <Field.Amount id={Name.Dispense.Payable} value={formData.dispense_payable_amount} title={Title.Payable} onChange={(e) => Utility.Handle.Calculate(e,setFormData,calculateFunction)} size={[1,34,0]} require={true}  />
                  </Control.Row>
                </Control.Column>
              </Control.Flex>
            </Control.Tab.Content>
              
            <Control.Tab.Content id={Code.Tab.Tab2} isActive={activeTab === Code.Tab.Tab2}>
              <Control.Row>
                <Field.Text id={Name.Dispense.PoNumber} value={formData.dispense_po_number} title={"PO No."} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,24,1]} />
                <Field.Text id={Name.Dispense.RefNo} value={formData.dispense_ref_number} title={Title.Refno} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,26,1]} />
                <Field.FilterSelect id={Name.Common.TermId} value={formData.term_id} title={"Terms & Conditions"} options={emptyOptions} setFormData={setFormData} size={[1,26,1]} />
                <Field.FilterSelect id={Name.Address.Id} value={formData.address_id} title={"Dispense Address"} options={emptyOptions} setFormData={setFormData} size={[1,24,0]}  />
              </Control.Row>
              <Control.Row>
                <Field.Area id={Name.Dispense.Comment} value={formData.dispense_comment} title={Title.Comment} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Area id={Name.Dispense.Comment} value={formData.dispense_comment} title={"New Dispense Address"} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Area id={Name.Dispense.PrintableNote} value={formData.dispense_printable_note} title={"Printable Notes"} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Area id={Name.Dispense.InternalNote} value={formData.dispense_internal_note} title={"Internal Note"} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
            </Control.Tab.Content>
          
          </Control.Form.Data>
        </form>
      )}
      {showMenu && (<div ref={contextMenuRef}><Control.FormContextMenu keyId={keyId} activeId={activeId} showMenu={showMenu} formName={formName}  handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} print={true}  /></div>)}
      <Actions.Form />
    </Control.Form.Frame>
  );
};

export default DispenseForm;